import jsPDF from "jspdf";

// ─────────────────────────────────────────────
//  UTILITIES
// ─────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  } catch {
    return dateString;
  }
};

const formatDateLong = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

const cleanText = (text) => {
  if (!text) return "";
  let cleaned = String(text);
  cleaned = cleaned.replace(/<[^>]*>/g, "");
  cleaned = cleaned.replace(/&nbsp;/g, " ");
  cleaned = cleaned.replace(/&amp;/g, "&");
  cleaned = cleaned.replace(/&lt;/g, "<");
  cleaned = cleaned.replace(/&gt;/g, ">");
  cleaned = cleaned.replace(/&quot;/g, '"');
  cleaned = cleaned.replace(/&#39;/g, "'");
  cleaned = cleaned.replace(/\s+/g, " ");
  return cleaned.trim();
};

const getImageUrl = (item) => {
  if (!item) return null;
  if (typeof item === "string") return item;
  if (typeof item === "object") {
    return item.data || item.url || item.src || item.base64 || item.path || item.file || null;
  }
  return null;
};

// Helper to retrieve logged-in student details from localStorage if props are incomplete
const getFallbackStudentProfile = () => {
  try {
    const raw = localStorage.getItem("userData") || localStorage.getItem("user") || "{}";
    const u = JSON.parse(raw);
    const p = u.profile || u;
    const firstName = u.first_name || p.first_name || "";
    const lastName = u.last_name || p.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim() || p.student_name || u.username || "N/A";

    const deptName =
      p.student_department_name ||
      p.student_department?.name ||
      p.department_name ||
      p.department ||
      u.department ||
      "";

    return {
      name: fullName,
      registration_number: p.student_id || p.registration_number || u.username || "N/A",
      email: u.email || p.email || "N/A",
      phone_number: p.phone_number || p.phone || u.phone || "",
      course: p.course || u.course || "",
      department: deptName,
      is_intern: Boolean(p.is_intern || u.is_intern || false),
    };
  } catch {
    return {};
  }
};

// ─────────────────────────────────────────────
//  COLOR PALETTE & STYLES
// ─────────────────────────────────────────────

const C = {
  navy: [31, 41, 55],
  blue: [37, 99, 235],
  white: [255, 255, 255],
  pageBg: [248, 249, 252],
  rowAlt: [249, 250, 251],
  tableHeader: [31, 41, 55],
  textPrimary: [17, 24, 39],
  textSecondary: [75, 85, 99],
  textTertiary: [156, 163, 175],
  border: [180, 188, 200],
  borderStrong: [100, 116, 139],
  divider: [203, 213, 225],
};

const S = {
  margin: 14,
  lineH: 4,
  headingSz: 9,
  bodySz: 8,
  smallSz: 7,
  tinySz: 6,
  tableSz: 8.5,
  tableBorderW: 0.6,
};

// ─────────────────────────────────────────────
//  MAIN EXPORT GENERATOR
// ─────────────────────────────────────────────

const generateActivityPDF = async (filteredActivitiesInput, userProfileInput = {}) => {
  // Extract activities array
  let filteredActivities = [];
  if (Array.isArray(filteredActivitiesInput)) {
    filteredActivities = filteredActivitiesInput;
  } else if (filteredActivitiesInput?.results && Array.isArray(filteredActivitiesInput.results)) {
    filteredActivities = filteredActivitiesInput.results;
  } else if (filteredActivitiesInput?.activities && Array.isArray(filteredActivitiesInput.activities)) {
    filteredActivities = filteredActivitiesInput.activities;
  }

  // Merge profile data with localStorage fallbacks to guarantee non-N/A fields
  const fallback = getFallbackStudentProfile();
  const profile = {
    name: userProfileInput.name || fallback.name || "N/A",
    registration_number: userProfileInput.registration_number || userProfileInput.student_id || fallback.registration_number || "N/A",
    email: userProfileInput.email || fallback.email || "N/A",
    phone_number: userProfileInput.phone_number || userProfileInput.phone || fallback.phone_number || "",
    course: (userProfileInput.course || fallback.course || "").toUpperCase().trim(),
    department: userProfileInput.department || fallback.department || "",
    is_intern: Boolean(userProfileInput.is_intern || fallback.is_intern || false),
  };

  const courseClean = (profile.course || "").toUpperCase().trim();
  const isIntern = Boolean(profile.is_intern || courseClean === "INTERNSHIP");
  const isPGOrSSPG =
    ["PG", "POST GRADUATE", "SUPER_SPECIALITY", "SUPER SPECIALITY", "SSPG", "DIPLOMA", "MD", "MS", "DM", "MCH"].includes(courseClean) ||
    Boolean(profile.department);
  const isMBBS = (!isIntern) && (!isPGOrSSPG);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const PW = doc.internal.pageSize.width;
  const PH = doc.internal.pageSize.height;
  const CW = PW - 2 * S.margin;
  const HEADER_H = 11;
  const FOOTER_H = 8;

  // Header/Footer renderer
  const addHeaderFooter = (pageNum) => {
    doc.setFillColor(...C.navy);
    doc.rect(0, 0, PW, HEADER_H, "F");

    if (pageNum > 1) {
      doc.setFontSize(S.smallSz);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("STUDENT ACTIVITY REPORT", PW / 2, 7, { align: "center" });
    }

    doc.setFontSize(S.tinySz);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C.textTertiary);
    doc.text(`Page ${pageNum}`, PW / 2, PH - 4, { align: "center" });
    doc.setTextColor(...C.textPrimary);
  };

  const checkPageBreak = (yPos, requiredSpace) => {
    if (yPos + requiredSpace > PH - FOOTER_H - 5) {
      doc.addPage();
      doc.setFillColor(...C.pageBg);
      doc.rect(0, 0, PW, PH, "F");
      addHeaderFooter(doc.internal.getNumberOfPages());
      return HEADER_H + S.margin;
    }
    return yPos;
  };

  const addSectionHeading = (title, y) => {
    y = checkPageBreak(y, 14);
    doc.setFontSize(S.headingSz);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.textPrimary);
    doc.text(title, S.margin, y + 4);
    doc.setFont("helvetica", "normal");
    doc.setDrawColor(...C.divider);
    doc.setLineWidth(0.3);
    doc.line(S.margin, y + 6, S.margin + CW, y + 6);
    return y + 10;
  };

  const addImageToPdf = async (imageSrc, x, y, maxW, maxH) => {
    if (!imageSrc) return y;
    try {
      const imgObj = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = imageSrc;
      });

      if (!imgObj || !imgObj.width) return y;

      let w = maxW;
      let h = (imgObj.height / imgObj.width) * w;
      if (h > maxH) {
        h = maxH;
        w = (imgObj.width / imgObj.height) * h;
      }

      const format = String(imageSrc).startsWith("data:image/png") ? "PNG" : "JPEG";
      doc.addImage(imageSrc, format, x, y, w, h);
      return y + h + 4;
    } catch (err) {
      console.warn("Failed to add image to PDF:", err);
      return y;
    }
  };

  // ─────────────────────────────────────────
  //  COVER PAGE (UPDATED PER SPECIFICATION)
  // ─────────────────────────────────────────

  const addCoverPage = () => {
    doc.setFillColor(...C.pageBg);
    doc.rect(0, 0, PW, PH, "F");

    let y = 24;

    // 1. Activity Report Badge (No OUHS Header / Logo)
    const reportDateStr = formatDateLong(new Date().toISOString());
    const badgeLabel = `ACTIVITY REPORT — ${reportDateStr}`;
    const badgeW = 140;
    const badgeX = (PW - badgeW) / 2;
    doc.setFillColor(...C.navy);
    doc.roundedRect(badgeX, y, badgeW, 14, 2, 2, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(badgeLabel, PW / 2, y + 9, { align: "center" });
    y += 24;

    // 2. Student Details Table
    const tableX = S.margin + 8;
    const tableW = CW - 16;
    const labelColW = tableW * 0.38;
    const valueColW = tableW * 0.62;
    const rowH = 9;
    const headerH = 9;

    // Contact Email / Phone formatting
    const contactText = [profile.email !== "N/A" ? profile.email : "", profile.phone_number]
      .filter(Boolean)
      .join(" | ") || "N/A";

    const tableRows = [
      ["Student Name", cleanText(profile.name)],
      ["Roll / Registration No.", cleanText(profile.registration_number)],
    ];

    // Department included ONLY for Post Graduate (PG) and Super Speciality (SSPG) students (non-MBBS with department)
    if (!isMBBS && profile.department) {
      tableRows.push(["Department", cleanText(profile.department)]);
    }

    // Institution removed completely per specification
    tableRows.push(
      ["Contact Email / Phone", cleanText(contactText)],
      ["Reporting Period", "Complete Activity History"],
      ["Generated Date & Time", new Date().toLocaleString("en-IN")]
    );

    const totalTableH = headerH + tableRows.length * rowH;

    doc.setDrawColor(...C.borderStrong);
    doc.setLineWidth(S.tableBorderW);
    doc.rect(tableX, y, tableW, totalTableH);

    doc.setFillColor(...C.tableHeader);
    doc.rect(tableX, y, labelColW, headerH, "F");
    doc.setFillColor(...C.tableHeader);
    doc.rect(tableX + labelColW, y, valueColW, headerH, "F");

    doc.setDrawColor(...C.borderStrong);
    doc.setLineWidth(S.tableBorderW);
    doc.line(tableX + labelColW, y, tableX + labelColW, y + headerH);
    doc.line(tableX, y + headerH, tableX + tableW, y + headerH);

    doc.setFontSize(S.tableSz);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("Student Details", tableX + labelColW / 2, y + headerH - 3, { align: "center" });
    doc.text("Information", tableX + labelColW + valueColW / 2, y + headerH - 3, { align: "center" });

    tableRows.forEach((row, i) => {
      const rowY = y + headerH + i * rowH;
      const isAlt = i % 2 === 1;

      doc.setFillColor(...(isAlt ? C.rowAlt : C.white));
      doc.rect(tableX, rowY, tableW, rowH, "F");

      doc.setDrawColor(...C.borderStrong);
      doc.setLineWidth(S.tableBorderW);
      doc.line(tableX, rowY + rowH, tableX + tableW, rowY + rowH);
      doc.line(tableX + labelColW, rowY, tableX + labelColW, rowY + rowH);
      doc.line(tableX, rowY, tableX, rowY + rowH);
      doc.line(tableX + tableW, rowY, tableX + tableW, rowY + rowH);

      doc.setFontSize(S.tableSz);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C.textSecondary);
      doc.text(row[0], tableX + 4, rowY + rowH - 3);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C.textPrimary);
      const valLines = doc.splitTextToSize(row[1], valueColW - 6);
      doc.text(valLines[0], tableX + labelColW + 4, rowY + rowH - 3);
    });

    y += totalTableH + 10;

    doc.setFontSize(S.smallSz);
    doc.setTextColor(...C.blue);
    doc.setFont("helvetica", "bold");
    doc.text(`Total Activities: ${filteredActivities.length}`, PW / 2, y, { align: "center" });

    addHeaderFooter(1);
  };

  // ─────────────────────────────────────────
  //  DRAWINGS SECTION
  // ─────────────────────────────────────────

  const addDrawingsSection = async (activity, yPos) => {
    const drawingsList = [];
    if (activity.drawing) drawingsList.push(activity.drawing);
    if (Array.isArray(activity.drawings)) drawingsList.push(...activity.drawings);

    const validDrawings = Array.from(new Set(drawingsList.map(getImageUrl).filter(Boolean)));
    if (validDrawings.length === 0) return yPos;

    yPos = addSectionHeading("DRAWINGS & CLINICAL SKETCHES", yPos);

    for (let i = 0; i < validDrawings.length; i++) {
      const drawing = validDrawings[i];
      yPos = checkPageBreak(yPos, 52);

      const maxW = CW - 10;
      const maxH = 50;
      const drawX = S.margin + (CW - maxW) / 2;

      const newY = await addImageToPdf(drawing, drawX, yPos + 2, maxW, maxH);
      yPos = newY > yPos ? newY + 4 : yPos + 5;
    }

    return yPos + 4;
  };

  // ─────────────────────────────────────────
  //  UPLOADED IMAGES SECTION (FIXED FOR ALL IMAGES, E.G. 5 IMAGES)
  // ─────────────────────────────────────────

  const addImagesSection = async (activity, yPos) => {
    const rawImagesList = [];
    if (activity.image) {
      if (Array.isArray(activity.image)) rawImagesList.push(...activity.image);
      else rawImagesList.push(activity.image);
    }
    if (Array.isArray(activity.images)) rawImagesList.push(...activity.images);
    if (Array.isArray(activity.media)) rawImagesList.push(...activity.media);
    if (Array.isArray(activity.photos)) rawImagesList.push(...activity.photos);

    // Extract valid Base64 / URL strings from string or object wrappers
    const validImages = Array.from(
      new Set(rawImagesList.map(getImageUrl).filter((url) => typeof url === "string" && url.trim().length > 0))
    );

    if (validImages.length === 0) return yPos;

    yPos = addSectionHeading(`ATTACHED IMAGES & EVIDENCE (${validImages.length})`, yPos);

    const cols = 4;
    const gap = 3;
    const imgW = (CW - (cols - 1) * gap) / cols; // ~43.25mm for 4 images per row
    const maxH = 42; // Compact height per row

    for (let i = 0; i < validImages.length; i += cols) {
      // Check page break with actual required height (45mm) so images don't jump to next page unnecessarily
      yPos = checkPageBreak(yPos, 45);

      const rowImages = validImages.slice(i, i + cols);
      let maxEndY = yPos;

      for (let colIdx = 0; colIdx < rowImages.length; colIdx++) {
        const imgUrl = rowImages[colIdx];
        const imgX = S.margin + colIdx * (imgW + gap);
        const endY = await addImageToPdf(imgUrl, imgX, yPos, imgW, maxH);
        if (endY > maxEndY) {
          maxEndY = endY;
        }
      }

      yPos = maxEndY > yPos ? maxEndY + 3 : yPos + 4;
    }

    return yPos + 4;
  };

  // ─────────────────────────────────────────
  //  ATTACHED DOCUMENTS SECTION
  // ─────────────────────────────────────────

  const addDocumentsSection = (activity, yPos) => {
    const docsList = [];
    if (activity.document) {
      if (Array.isArray(activity.document)) docsList.push(...activity.document);
      else docsList.push(activity.document);
    }
    if (Array.isArray(activity.documents)) docsList.push(...activity.documents);

    const validDocs = docsList.filter(Boolean);
    if (validDocs.length === 0) return yPos;

    yPos = addSectionHeading("ATTACHED DOCUMENTS & FILE RECORDS", yPos);

    for (let i = 0; i < validDocs.length; i++) {
      const docItem = validDocs[i];
      yPos = checkPageBreak(yPos, 14);

      const docName = cleanText(
        typeof docItem === "string" ? docItem : docItem.name || docItem.filename || `Document #${i + 1}`
      );
      const docType = cleanText(
        typeof docItem === "object" ? docItem.type || docItem.extension || "Attachment" : "File"
      );

      doc.setFillColor(...C.rowAlt);
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.3);
      doc.roundedRect(S.margin, yPos, CW, 10, 1.5, 1.5, "FD");

      doc.setFontSize(S.bodySz);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C.textPrimary);
      doc.text(`[FILE] ${docName}`, S.margin + 4, yPos + 6.5);

      doc.setFontSize(S.tinySz);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C.textSecondary);
      doc.text(`${docType.toUpperCase()} • Attached`, S.margin + CW - 4, yPos + 6.5, { align: "right" });

      yPos += 13;
    }

    return yPos + 4;
  };

  // ─────────────────────────────────────────
  //  ACTIVITY ENTRY LOOP
  // ─────────────────────────────────────────

  // ─────────────────────────────────────────
  //  FACULTY ASSESSMENT & REACTION SECTION
  // ─────────────────────────────────────────

  const addFacultyAssessmentSection = async (act, yPos) => {
    yPos = addSectionHeading("FACULTY & HOD EVALUATION REVIEWS", yPos);

    const facultyComment = act.comments?.find((c) => c.from_source !== "HOD") || act.comments?.[0];
    const facultyReaction = act.reactions?.find((r) => r.from_source !== "HOD") || act.reactions?.[0];

    const hodComment = act.comments?.find((c) => c.from_source === "HOD");
    const hodReaction = act.reactions?.find((r) => r.from_source === "HOD");

    // Layout constants for Side-by-Side 50/50 split
    const margin = S.margin;
    const gap = 4;
    const boxW = (CW - gap) / 2; // Split width equally
    let boxH = 26; // Base compact height

    const getReviewData = (commentObj, reactionObj, isHod = false) => {
      const exists = !!commentObj || !!reactionObj;
      const isApproved = reactionObj?.type === "✅" || reactionObj?.decision === "Approved" || commentObj?.status === "✅" || commentObj?.assessment?.facultyDecision === "Approved";
      const isRejected = reactionObj?.type === "❌" || reactionObj?.decision === "Rejected" || commentObj?.status === "❌" || commentObj?.assessment?.facultyDecision === "Rejected";

      const decisionText = isApproved ? "APPROVED ✅" : isRejected ? "REJECTED ❌" : exists ? "ASSESSED" : "PENDING ⏳";
      const evalName = cleanText(commentObj?.from || reactionObj?.from || (isHod ? "HOD" : "Faculty"));
      const evalSubrole = cleanText(commentObj?.subrole || reactionObj?.subrole || (isHod ? "Head of Department (HOD)" : "Faculty Member"));
      const evalDate = formatDateLong(commentObj?.timestamp || reactionObj?.timestamp);
      const ratingVal = cleanText(commentObj?.assessment?.rating || reactionObj?.rating);
      const scoreVal = (commentObj?.assessment?.numericScore !== undefined && commentObj?.assessment?.numericScore !== null && commentObj?.assessment?.numericScore !== "")
        ? commentObj.assessment.numericScore
        : reactionObj?.score;
      const feedbackText = cleanText(commentObj?.text || "");
      const signatureData = commentObj?.signature || reactionObj?.signature || null;

      let bgCol = [248, 250, 252];       // Default light slate
      let borderCol = [203, 213, 225];
      let headerTextCol = [71, 85, 105];

      if (isApproved) {
        bgCol = [240, 253, 244];         // Green
        borderCol = [134, 239, 172];
        headerTextCol = [21, 128, 61];
      } else if (isRejected) {
        bgCol = [254, 242, 242];         // Red
        borderCol = [252, 165, 165];
        headerTextCol = [185, 28, 28];
      } else if (exists) {
        bgCol = [254, 243, 199];         // Amber
        borderCol = [253, 230, 138];
        headerTextCol = [180, 83, 9];
      }

      return {
        exists,
        isApproved,
        isRejected,
        decisionText,
        evalName,
        evalSubrole,
        evalDate,
        ratingVal,
        scoreVal,
        feedbackText,
        signatureData,
        bgCol,
        borderCol,
        headerTextCol
      };
    };

    const facData = getReviewData(facultyComment, facultyReaction, false);
    const hodData = getReviewData(hodComment, hodReaction, true);

    // Calculate required box height based on feedback lines
    const facFeedbackLines = facData.feedbackText ? doc.splitTextToSize(`Feedback: ${facData.feedbackText}`, boxW - 8) : [];
    const hodFeedbackLines = hodData.feedbackText ? doc.splitTextToSize(`Feedback: ${hodData.feedbackText}`, boxW - 8) : [];

    const maxFeedbackLines = Math.max(facFeedbackLines.length, hodFeedbackLines.length);
    if (maxFeedbackLines > 0) {
      boxH += maxFeedbackLines * 3.5 + 2;
    }
    if (facData.signatureData || hodData.signatureData) {
      boxH += 8;
    }

    yPos = checkPageBreak(yPos, boxH + 6);

    const drawSideBox = async (xPos, title, data) => {
      doc.setFillColor(...data.bgCol);
      doc.setDrawColor(...data.borderCol);
      doc.setLineWidth(0.4);
      doc.roundedRect(xPos, yPos, boxW, boxH, 2, 2, "FD");

      let textY = yPos + 5;
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...data.headerTextCol);
      doc.text(`${title.toUpperCase()}: ${data.decisionText}`, xPos + 4, textY);

      textY += 4.5;
      doc.setFontSize(S.tinySz);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C.textPrimary);
      doc.text(`By ${data.evalSubrole}`, xPos + 4, textY);

      if (data.exists) {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...C.textSecondary);
        doc.text(`${data.evalName}${data.evalDate ? ` (${data.evalDate})` : ''}`, xPos + 4, textY + 3.8);
      }

      textY += 8;

      if (data.exists) {
        let metaStr = "";
        if (data.ratingVal) metaStr += `Rating: ${data.ratingVal}`;
        if (data.scoreVal !== undefined && data.scoreVal !== null && data.scoreVal !== "") {
          metaStr += metaStr ? ` | Score: ${data.scoreVal}/10` : `Score: ${data.scoreVal}/10`;
        }
        if (metaStr) {
          doc.setFontSize(S.tinySz);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...C.textPrimary);
          doc.text(metaStr, xPos + 4, textY);
          textY += 4.5;
        }

        const lines = data.feedbackText ? doc.splitTextToSize(`Feedback: ${data.feedbackText}`, boxW - 8) : [];
        if (lines.length > 0) {
          doc.setFontSize(S.tinySz);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...C.textPrimary);
          doc.text(lines, xPos + 4, textY);
        }

        if (data.signatureData) {
          try {
            const sigY = yPos + boxH - 8;
            doc.setFontSize(6);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(...C.textSecondary);
            doc.text("E-Sig:", xPos + 4, sigY + 4);
            await addImageToPdf(data.signatureData, xPos + 22, sigY - 1, 24, 6);
          } catch (err) {
            console.warn("Signature embed error:", err);
          }
        }
      } else {
        doc.setFontSize(S.tinySz);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(...C.textSecondary);
        doc.text("No evaluation recorded", xPos + 4, textY);
      }
    };

    await drawSideBox(margin, "Faculty Reaction", facData);
    await drawSideBox(margin + boxW + gap, "HOD Reaction", hodData);

    return yPos + boxH + 6;
  };

  addCoverPage();

  for (let idx = 0; idx < filteredActivities.length; idx++) {
    const act = filteredActivities[idx];

    doc.addPage();
    doc.setFillColor(...C.pageBg);
    doc.rect(0, 0, PW, PH, "F");

    const pageNum = doc.internal.getNumberOfPages();
    addHeaderFooter(pageNum);

    let y = HEADER_H + S.margin;

    // Activity Title Card
    const titleText = `ACTIVITY #${idx + 1}: ${cleanText(act.title || "Untitled Activity").toUpperCase()}`;
    doc.setFillColor(...C.navy);
    doc.roundedRect(S.margin, y, CW, 10, 1.5, 1.5, "F");

    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(titleText, S.margin + 4, y + 6.5);
    y += 15;

    // General Information
    y = addSectionHeading("GENERAL INFORMATION", y);

    const formatCompStr = (actObj) => {
      if (Array.isArray(actObj.competencies) && actObj.competencies.length > 0) {
        return actObj.competencies.map((c) => `${c.code}: ${c.description}`).join("; ");
      }
      return actObj.competencyAddress || actObj.competency_address || "N/A";
    };

    const genInfoFields = [
      ["Activity Type", act.type || "N/A"],
      ["Date Performed", formatDateLong(act.date)],
    ];

    if (isMBBS) {
      genInfoFields.push(["Competencies", formatCompStr(act)]);
    }

    genInfoFields.push(["Attempt Type", act.attemptType || act.attempt_type || "N/A"]);

    if (act.procedureCategory || act.procedure_category) {
      genInfoFields.push(["Procedure Category", act.procedureCategory || act.procedure_category]);
    }
    if (act.procedureSubtype || act.procedure_sub_type) {
      genInfoFields.push(["Procedure", act.procedureSubtype || act.procedure_sub_type]);
    }

    genInfoFields.forEach(([lbl, val]) => {
      y = checkPageBreak(y, 8);
      doc.setFontSize(S.bodySz);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C.textSecondary);
      const lw = doc.getTextWidth(`${lbl}: `);
      doc.text(`${lbl}:`, S.margin, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C.textPrimary);
      const valClean = cleanText(val || "N/A");
      const lines = doc.splitTextToSize(valClean, CW - lw - 5);
      doc.text(lines, S.margin + lw, y);
      y += lines.length * S.lineH + 1;
    });

    y += 4;

    // Description & Log Details
    y = addSectionHeading("ACTIVITY DESCRIPTION & LOG DETAILS", y);
    const descText = cleanText(act.content) || "No detailed log content provided.";
    const descLines = doc.splitTextToSize(descText, CW);
    y = checkPageBreak(y, descLines.length * S.lineH + 6);
    doc.setFontSize(S.bodySz);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C.textPrimary);
    doc.text(descLines, S.margin, y);
    y += descLines.length * S.lineH + 6;

    // Render Drawings, Images, and Documents
    y = await addDrawingsSection(act, y);
    y = await addImagesSection(act, y);
    y = addDocumentsSection(act, y);

    // Render Faculty Assessment & Review Card with visual status colors
    y = await addFacultyAssessmentSection(act, y);
  }

  const fileDateStr = new Date().toISOString().split("T")[0];
  doc.save(`Student_Activity_Report_${fileDateStr}.pdf`);
};

export default generateActivityPDF;
export { generateActivityPDF };
